import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar,
} from '@mui/material';
import { Field } from '../../UI';
import { quebrarAcordo } from '../../../../services/core/processos/processosAcordosService';
import { findMotivosQuebraAcordoListAction } from '../../../../store/Global/LoadSelectors/loadSelectors.saga';
import useSessaoDialogMutacao from '../../../hooks/useSessaoDialogMutacao';

const MENSAGEM_ERRO_GENERICA = 'Não foi possível quebrar o Acordo. Tente novamente.';

/**
 * Dialog "Mãozinha cortada" — caso default (Quebrar Acordo, AW2-1133 Etapa 7)
 * — port de `DialogQuebrarAcordo.jsx` real. Diferente de `DialogFecharAcordo.jsx`
 * (Etapa 6, fecha IMEDIATAMENTE ao confirmar), a tela real deste fluxo
 * (`onClickSubmitQuebrarAcordo`) só alterna a visibilidade no `finally`,
 * DEPOIS do `await`, e faz isso incondicionalmente (sucesso OU erro) —
 * replicado aqui com `enviando` desabilitando os controles durante a chamada
 * e `onClose()` disparado nos 2 desfechos (guardado por `sessaoRef`), nunca
 * antes do `try`.
 *
 * Motivo (`idMotivoQuebraAcordo`) vem do MESMO selector Redux global já
 * carregado pela tela real (`state.selectors.motivosQuebraAcordo`,
 * `findMotivosQuebraAcordoListAction`) — sem criar um selector novo,
 * reaproveitando a infraestrutura existente (mesmo padrão de
 * `motivosBaixaSelectorItems` em `DialogEnviarOutraFase.jsx`). Fetch
 * guardado por `open` — este dialog fica sempre montado (só `open` alterna).
 */
export default function DialogQuebrarAcordo({
  open, acordo, idProcesso, idFaseProcesso, onClose, onMutado,
}) {
  const dispatch = useDispatch();
  const motivosQuebraAcordoOptions = useSelector(states => states.selectors.motivosQuebraAcordo.selector);

  const [idMotivoQuebraAcordo, setIdMotivoQuebraAcordo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const { sessaoRef, enviandoRef } = useSessaoDialogMutacao(open, idProcesso, () => {
    setIdMotivoQuebraAcordo('');
    setEnviando(false);
    setResultado(null);
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    dispatch(findMotivosQuebraAcordoListAction());
  }, [open, dispatch]);

  const onFecharResultado = () => setResultado(null);

  const onConfirmar = async () => {
    if (enviandoRef.current || !acordo || !idMotivoQuebraAcordo) {
      return;
    }

    const sessaoAlvo = sessaoRef.current;
    const payload = {
      idProcesso, idFase: idFaseProcesso, idAcordo: acordo.id, motivoQuebra: idMotivoQuebraAcordo,
    };
    enviandoRef.current = true;
    setEnviando(true);

    try {
      await quebrarAcordo(payload);
      if (sessaoRef.current === sessaoAlvo) {
        onClose();
        setResultado({ severidade: 'success', mensagem: 'Acordo quebrado com sucesso.' });
      }
    } catch {
      if (sessaoRef.current === sessaoAlvo) {
        onClose();
        setResultado({ severidade: 'error', mensagem: MENSAGEM_ERRO_GENERICA });
      }
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        enviandoRef.current = false;
        setEnviando(false);
      }
      // Incondicional (mesmo fora da sessão alvo) — mesmo raciocínio de
      // `DialogFecharAcordo.jsx`: `onMutado` lê o `idProcesso` ATUAL do
      // container, nunca um valor capturado aqui.
      if (onMutado) {
        onMutado();
      }
    }
  };

  return (
    <>
      <Dialog open={open} onClose={enviando ? undefined : onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Quebra de Acordo</DialogTitle>
        <DialogContent>
          <Field
            label="Motivo*"
            tipo="select"
            options={motivosQuebraAcordoOptions}
            value={idMotivoQuebraAcordo}
            onChange={e => setIdMotivoQuebraAcordo(e.target.value)}
            disabled={enviando}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button onClick={onConfirmar} variant="contained" disabled={enviando || !idMotivoQuebraAcordo}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(resultado)}
        autoHideDuration={4000}
        onClose={onFecharResultado}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={resultado?.severidade ?? 'info'} variant="filled" onClose={onFecharResultado}>
          {resultado?.mensagem}
        </Alert>
      </Snackbar>
    </>
  );
}

DialogQuebrarAcordo.propTypes = {
  open: PropTypes.bool,
  acordo: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  idFaseProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
  onMutado: PropTypes.func,
};

DialogQuebrarAcordo.defaultProps = {
  open: false,
  acordo: null,
  idProcesso: null,
  idFaseProcesso: null,
  onMutado: null,
};
